import * as React from "react";
import { observer } from "mobx-react";

import { UitkTertiaryButton } from "uitk-react-button";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";

import { useLocalization } from "@shared-ui/localization-context";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import { FlightSubLOBProps } from "components/flexComponents/WizardFlightPWA/typings";
import { locKeys } from "components/flexComponents/WizardFlightPWA/components/l10n";
import { MultiCityFlightLeg } from "components/flexComponents/WizardFlightPWA/components/MultiCityFlightLeg";
import { FlightPreferences } from "components/flexComponents/WizardFlightPWA/components/FlightPreferences";

import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";
import { Columns } from "components/shared/StorefrontWizard/typings";
import { LocationType } from "src/components/shared/Location/components/LocationType";

export const MulticityFlight = observer((props: FlightSubLOBProps) => {
  const [linkDisabled, setLinkDisabled] = React.useState(true);
  const {
    halfWizard,
    defaultMultipleDestination,
    defaultDestination,
    defaultMultipleOrigin,
    defaultOrigin,
    defaultStopover,
    defaultMultipleStopover,
  } = props.wizardState.globalWizardState.config;
  const { DESTINATION, ORIGIN } = LocationType;

  if ((Array.isArray(defaultMultipleDestination) || defaultDestination) && props.lobState.location[DESTINATION]) {
    props.lobState.location[DESTINATION].value = "";
  }

  if ((Array.isArray(defaultMultipleOrigin) || defaultOrigin) && props.lobState.location[ORIGIN]) {
    props.lobState.location[ORIGIN].value = "";
  }

  if (defaultStopover) {
    props.lobState.location.destination.value = defaultStopover.split(":")[0];
  }
  if (Array.isArray(defaultMultipleStopover)) {
    props.lobState.location.destination.value = defaultMultipleStopover[0].split(":")[0];
  }

  const locationColumns: Columns = {
    fullWidth: undefined,
    half: {
      small: 2,
      medium: 12,
    },
  };

  const datesColumns: Columns = {
    fullWidth: undefined,
    half: {
      small: 2,
      medium: 12,
    },
  };

  /**
   * This hook is reached for rehydratation, here the "add a flight" link gets abled, if JS is disabled then this hook is never executed
   */
  React.useEffect(() => setLinkDisabled(false), []);

  const gridColumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 12 };
  const addFlightColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12 };
  const locationColSpan: UitkLayoutConditionalGridSpan = locationColumns[halfWizard ? "half" : "fullWidth"];
  const datesColSpan: UitkLayoutConditionalGridSpan = datesColumns[halfWizard ? "half" : "fullWidth"];

  const { errorInputRef, isMulticityFormValid, updateInvalidFormsState } = props.lobState;

  if (isMulticityFormValid) {
    updateInvalidFormsState();
  }

  const { formatText } = useLocalization();

  return (
    <UitkSpacing margin={{ blockstart: "three" }}>
      <UitkLayoutGrid columns={gridColumns} space="three">
        {!isMulticityFormValid && (
          <LobErrorSummary
            locHeadingToken={props.getLocError(false)!}
            locLinkToken={props.getLocError(true)}
            linkClickFocusId="multi-error"
            inputErrorRef={errorInputRef}
            colSpan={gridColumns}
            setInputsRefs={props.setInputsRefs}
          />
        )}
        {halfWizard && <FlightPreferences {...props} />}
        <MultiCityFlightLeg
          lobState={props.lobState}
          routeStore={props.routeStore}
          setInputsRefs={props.setInputsRefs}
          locationColSpan={locationColSpan}
          datesColSpan={datesColSpan}
          globalConfig={props.wizardState.globalWizardState.config}
        />

        <Viewport>
          <ViewSmall>
            <>
              {props.lobState.listOfAdditionalLegs.length <
                props.lobState.config.multiCityFlight.maxNumberOfAdditionalLegs && (
                <UitkLayoutGridItem colSpan={addFlightColSpan}>
                  <div>
                    <UitkTertiaryButton
                      icon="add"
                      text={formatText(locKeys.addFlightLabel)}
                      onClick={props.lobState.addALeg}
                      disabled={linkDisabled}
                    />
                  </div>
                </UitkLayoutGridItem>
              )}
            </>
          </ViewSmall>
          <ViewMedium>
            <>
              {props.lobState.listOfAdditionalLegs.length <
                props.lobState.config.multiCityFlight.maxNumberOfAdditionalLegs && (
                <UitkLayoutGridItem colSpan={addFlightColSpan} className="uitk-type-right">
                  <div>
                    <UitkTertiaryButton
                      icon="add"
                      text={formatText(locKeys.addFlightLabel)}
                      onClick={props.lobState.addALeg}
                      disabled={linkDisabled}
                    />
                  </div>
                </UitkLayoutGridItem>
              )}
            </>
          </ViewMedium>
        </Viewport>
      </UitkLayoutGrid>
    </UitkSpacing>
  );
});
